import React, { useEffect, useState } from 'react';

import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useSnackbar } from 'notistack';

import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ModeIcon from '@mui/icons-material/Mode';
import { Box, IconButton, Stack, Switch, Typography } from '@mui/material';

import { getSavedInclusionId, getSavedInclusionMode } from '~/hooks/apis/inclusions/packageInclusionMutationUtils';
import { useUpdatePackageInclusions } from '~/hooks/apis/inclusions/useUpdatePackageInclusions';

import getErrorMessage from '~/utils/getErrorMessage';
import { reportError } from '~/utils/reportError';

import InclusionAssetEditDialog, { type InclusionAssetEditValue } from '../../../Inclusions/InclusionAssetEditDialog';
import {
  type PackageInclusionRecord,
  getInclusionCategory,
  getInclusionName,
  getInclusionPersistId,
  getPackageInclusionSortOrder,
  isCustomInclusion,
  isInclusionHidden,
} from '../../../utils';

import { PACKAGE_INCLUSIONS_GRID_COLUMNS } from './utils';

interface Props {
  id: string;
  inclusion: PackageInclusionRecord;
  offerId?: string;
  packageId?: string;
  packageName: string;
  vendorId?: string;
  onChange: (nextInclusion: PackageInclusionRecord, options?: { moveToTop?: boolean }) => void;
  onUnassign: () => void;
}

function getDraftInclusion(inclusion: PackageInclusionRecord): InclusionAssetEditValue {
  return {
    category: getInclusionCategory(inclusion),
    categoryId: inclusion.inclusion?.category_id ?? null,
    currency: inclusion.inclusion?.currency ?? '',
    isHidden: isInclusionHidden(inclusion),
    name: getInclusionName(inclusion),
    requiresBooking: !!inclusion.inclusion?.requires_booking,
    rrp: inclusion.inclusion?.rrp ?? 0,
    type: inclusion.inclusion?.type ?? 'Flash',
    unit: inclusion.inclusion?.unit ?? '',
  };
}

function updateInclusionDetails(
  inclusion: PackageInclusionRecord,
  draftInclusion: InclusionAssetEditValue,
  persistId?: string,
): PackageInclusionRecord {
  if (inclusion.inclusion) {
    return {
      ...inclusion,
      inclusion_id: persistId ?? inclusion.inclusion_id,
      inclusion: {
        ...inclusion.inclusion,
        category_id: draftInclusion.categoryId,
        currency: draftInclusion.currency,
        ...(persistId ? { id: persistId, id_salesforce_external: null } : {}),
        is_hidden: draftInclusion.isHidden,
        name: draftInclusion.name,
        requires_booking: draftInclusion.requiresBooking,
        rrp: draftInclusion.rrp,
        type: draftInclusion.type,
        unit: draftInclusion.unit,
        category: {
          ...inclusion.inclusion.category,
          name: draftInclusion.category,
        },
      },
    };
  }

  return {
    ...inclusion,
    ...(persistId ? { inclusion_id: persistId } : {}),
    category: draftInclusion.category,
    name: draftInclusion.name,
  };
}

export default function PackageInclusionsTabRow({
  id,
  inclusion,
  offerId,
  packageId,
  packageName,
  vendorId,
  onChange,
  onUnassign,
}: Props) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const updatePackageInclusions = useUpdatePackageInclusions();
  const { enqueueSnackbar } = useSnackbar();
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [draftInclusion, setDraftInclusion] = useState<InclusionAssetEditValue>(() => getDraftInclusion(inclusion));
  const [isSavingRequiresBooking, setIsSavingRequiresBooking] = useState(false);
  const isHidden = isInclusionHidden(inclusion);
  const category = getInclusionCategory(inclusion);

  useEffect(() => {
    if (!isEditDialogOpen) {
      setDraftInclusion(getDraftInclusion(inclusion));
    }
  }, [inclusion, isEditDialogOpen]);

  const handleCloseEditDialog = () => {
    setDraftInclusion(getDraftInclusion(inclusion));
    setIsEditDialogOpen(false);
  };

  const saveInclusionEdit = async (nextDraft: InclusionAssetEditValue) => {
    const persistId = getInclusionPersistId(inclusion);

    if (!vendorId || !persistId || !offerId || !packageId) {
      onChange(updateInclusionDetails(inclusion, nextDraft));
      return undefined;
    }

    const response = await updatePackageInclusions.mutateAsync({
      inclusionIdToCustomise: persistId,
      offerId,
      packageInclusions: [
        {
          category_id: nextDraft.categoryId ?? null,
          currency: nextDraft.currency,
          inclusion_id: persistId,
          is_hidden: nextDraft.isHidden,
          lux_plus_tier: inclusion.lux_plus_tier,
          name: nextDraft.name,
          offer_id: offerId,
          opportunity_name: inclusion.opportunity_name,
          package_id: packageId,
          package_name: inclusion.package_name ?? packageName,
          requires_booking: nextDraft.requiresBooking,
          rrp: nextDraft.rrp,
          sort_order: getPackageInclusionSortOrder(inclusion),
          style: inclusion.style,
          type: nextDraft.type,
          unit: nextDraft.unit,
          vendor_salesforce_id: vendorId,
        },
      ],
    });

    const nextPersistId = getSavedInclusionId(response);

    if (!nextPersistId) {
      throw new Error('Saved inclusion response did not include an inclusion ID.');
    }

    const mode = getSavedInclusionMode(response);
    const customPersistId = mode === 'customised' ? nextPersistId : undefined;

    onChange(updateInclusionDetails(inclusion, nextDraft, customPersistId), {
      moveToTop: mode === 'customised',
    });

    return mode;
  };

  const handleSaveEditDialog = async () => {
    try {
      const mode = await saveInclusionEdit(draftInclusion);
      enqueueSnackbar(mode === 'customised' ? 'Custom inclusion created for this package' : 'Inclusion saved', {
        variant: 'success',
      });
      setIsEditDialogOpen(false);
    } catch (error) {
      reportError(error);
      enqueueSnackbar(getErrorMessage(error, 'Failed to save inclusion'), { variant: 'error' });
    }
  };

  const handleToggleRequiresBooking = async (checked: boolean) => {
    setIsSavingRequiresBooking(true);

    try {
      const mode = await saveInclusionEdit({ ...getDraftInclusion(inclusion), requiresBooking: checked });
      enqueueSnackbar(mode === 'customised' ? 'Custom inclusion created for this package' : 'Inclusion saved', {
        variant: 'success',
      });
    } catch (error) {
      reportError(error);
      enqueueSnackbar(getErrorMessage(error, 'Failed to save inclusion'), { variant: 'error' });
    } finally {
      setIsSavingRequiresBooking(false);
    }
  };

  const handleUnassignEditDialog = async () => {
    const packageInclusionId = inclusion.id ? String(inclusion.id) : undefined;

    if (!offerId || !packageInclusionId) {
      onUnassign();
      setIsEditDialogOpen(false);
      return;
    }

    try {
      await updatePackageInclusions.mutateAsync({
        offerId,
        packageInclusionIdsToDelete: [packageInclusionId],
        packageInclusions: [],
      });
      onUnassign();
      enqueueSnackbar('Inclusion unassigned from package', { variant: 'success' });
      setIsEditDialogOpen(false);
    } catch (error) {
      reportError(error);
      enqueueSnackbar(getErrorMessage(error, 'Failed to unassign inclusion from package'), { variant: 'error' });
      throw error;
    }
  };

  return (
    <>
      <Box
        ref={setNodeRef}
        sx={{
          alignItems: 'center',
          borderTop: 1,
          borderColor: 'divider',
          display: 'grid',
          gap: 2,
          gridTemplateColumns: PACKAGE_INCLUSIONS_GRID_COLUMNS,
          opacity: isDragging ? 0.72 : isHidden ? 0.58 : 1,
          px: 2,
          py: 2,
          transform: CSS.Transform.toString(transform),
          transition,
        }}
      >
        <IconButton
          size="small"
          aria-label={`Reorder ${getInclusionName(inclusion) || 'inclusion'}`}
          sx={{
            color: 'text.disabled',
            cursor: isDragging ? 'grabbing' : 'grab',
          }}
          {...attributes}
          {...listeners}
        >
          <DragIndicatorIcon fontSize="small" />
        </IconButton>

        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" color={isHidden ? 'text.disabled' : 'text.primary'}>
            {category}
          </Typography>
        </Box>

        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" color={isHidden ? 'text.disabled' : 'text.primary'}>
            {getInclusionName(inclusion)}
          </Typography>
        </Box>

        <Stack direction="row" justifyContent="center" sx={{ minWidth: 0 }}>
          <Switch
            size="small"
            checked={inclusion.style === 'highlighted'}
            onChange={(event) => onChange({ ...inclusion, style: event.target.checked ? 'highlighted' : null })}
            disabled={isHidden || !!inclusion.lux_plus_tier}
          />
        </Stack>

        <Stack direction="row" justifyContent="center" sx={{ minWidth: 0 }}>
          <Switch
            size="small"
            checked={inclusion.lux_plus_tier === 'base'}
            onChange={(event) => onChange({ ...inclusion, lux_plus_tier: event.target.checked ? 'base' : null })}
            disabled={isHidden || inclusion.style === 'highlighted' || inclusion.style === 'bonus'}
          />
        </Stack>

        <Stack direction="row" justifyContent="center" sx={{ minWidth: 0 }}>
          <Switch
            size="small"
            checked={!!inclusion.inclusion?.requires_booking}
            disabled={isSavingRequiresBooking}
            onChange={(event) => void handleToggleRequiresBooking(event.target.checked)}
          />
        </Stack>

        <Stack direction="row" justifyContent="center" spacing={0.5}>
          <IconButton color="primary" aria-label="Edit inclusion" onClick={() => setIsEditDialogOpen(true)}>
            <ModeIcon />
          </IconButton>
        </Stack>
      </Box>

      <InclusionAssetEditDialog
        isCustom={isCustomInclusion(inclusion)}
        isSaving={updatePackageInclusions.isPending}
        open={isEditDialogOpen}
        value={draftInclusion}
        onChange={setDraftInclusion}
        onClose={handleCloseEditDialog}
        onSave={() => void handleSaveEditDialog()}
        onUnassign={handleUnassignEditDialog}
        unassignConfirmationText={`Unassign ${getInclusionName(inclusion) || 'this inclusion'} from this package?`}
      />
    </>
  );
}
